import React, { useState, useRef, useEffect } from 'react';
import { readAndCompressImage } from 'browser-image-resizer'; // Import browser-image-resizer
import ReCAPTCHA from 'react-google-recaptcha'; // Import reCAPTCHA
import './Upload.css';
import { t } from '../i18n'; // NEW: import translations

// Configuration for resizing images
const imageResizeConfig = {
  quality: 0.8, // Image quality (0.1 to 1)
  maxWidth: 2048, // Maximum width
  maxHeight: 2048, // Maximum height
  autoRotate: true, // Automatically rotate based on EXIF data
};

const MAX_UPLOAD_SIZE = 25 * 1024 * 1024 * 1024; // 25GB in bytes

const Upload = () => {
  const [email, setEmail] = useState('');
  const [senderName, setSenderName] = useState('');
  const [otp, setOtp] = useState('');
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [files, setFiles] = useState([]);
  const [coverPhotos, setCoverPhotos] = useState([]); // State for cover photos
  const [step, setStep] = useState(1);
  const [uploading, setUploading] = useState(false);
  const [zippingProgress, setZippingProgress] = useState(0); // Zipping progress
  const [uploadProgress, setUploadProgress] = useState(0); // Upload progress
  const [statusMessage, setStatusMessage] = useState('');
  const [isProcessing, setIsProcessing] = useState(false); // Spinner state
  const [uploadCompleted, setUploadCompleted] = useState(false); // Upload completion state
  const [captchaToken, setCaptchaToken] = useState(null); // State for reCAPTCHA token
  const [totalFileSize, setTotalFileSize] = useState(0); // State for total file size
  const [exceedsLimit, setExceedsLimit] = useState(false); // State for exceeding limit
  const [termsAccepted, setTermsAccepted] = useState(false); // State for terms acceptance
  const [clientSendProgress, setClientSendProgress] = useState(0);
  const [clientSendTimeRemaining, setClientSendTimeRemaining] = useState(null);
  const [zippingTimeRemaining, setZippingTimeRemaining] = useState(null);
  const [uploadTimeRemaining, setUploadTimeRemaining] = useState(null);
  // NEW: overall ETA
  const [overallTimeRemaining, setOverallTimeRemaining] = useState(null);
  // NEW: current UI language (supported: 'en', 'he', 'ru')
  const [language, setLanguage] = useState('en');
  // combobox state + refs
  const [langOpen, setLangOpen] = useState(false);
  const langTriggerRef = useRef(null);
  const langListRef = useRef(null);
  const captchaRef = useRef(null); // NEW: ref for invisible captcha
  const langOptions = [
    { code: 'en', label: 'English', flag: `${process.env.PUBLIC_URL || ''}/flags/us.svg`, alt: 'US flag' },
    { code: 'he', label: 'עברית', flag: `${process.env.PUBLIC_URL || ''}/flags/il.svg`, alt: 'Israel flag' },
    { code: 'ru', label: 'Русский', flag: `${process.env.PUBLIC_URL || ''}/flags/ru.svg`, alt: 'Russia flag' }
  ];

  const zippingStartTime = useRef(null); // Ref to track zipping start time
  const uploadStartTime = useRef(null); // Ref to track upload start time
  // New session + streamed metrics
  const [sessionId, setSessionId] = useState(null);
  const [zippedBytes, setZippedBytes] = useState(0);
  const [uploadedBytes, setUploadedBytes] = useState(0);
  const [uploadAuthToken, setUploadAuthToken] = useState(null); // NEW: store upload auth token

  // NEW refs for throttled ETA + speeds
  const clientSendStartTime = useRef(null);
  const clientBytesRef = useRef({ loaded: 0, total: 0, lastLoaded: 0, lastTime: 0, speed: 0, remaining: null });
  const zipSpeedRef = useRef({ bytes: 0, lastBytes: 0, lastTime: 0, speed: 0, remaining: null });
  const uploadSpeedRef = useRef({ bytes: 0, lastBytes: 0, lastTime: 0, speed: 0, remaining: null });
  const lastEtaUpdateRef = useRef(0);
  const simulationRef = useRef(null);
  const hasServerProgressRef = useRef(false); // ADD missing ref used throughout for server progress detection

  const partSimRef = useRef({
    inFlight: false,
    partNumber: null,
    partSize: 0,
    startTime: 0,
    durationMs: null,
    lastRealUploadedBytes: 0,
    intervalId: null,
    simulatedPct: 0,
    lastPartSpeed: null
  });
  const [partProgressPct, setPartProgressPct] = useState(0);
  // ADD missing refs for stable ETA phase starts
  const serverZipStartRef = useRef(null);
  const serverUploadStartRef = useRef(null);
  // Added missing refs for unified ETA smoothing + phase starts
  const clientPhaseStartRef = useRef(null);
  const zipPhaseStartRef = useRef(null);
  const uploadPhaseStartRef = useRef(null);
  const smoothedRemainingRef = useRef(null);
  const overallStartRef = useRef(null); // NEW overall process start

  const SERVER_URL = process.env.REACT_APP_SERVER_URL || 'http://localhost:5000';

  const handleEmailChange = (e) => setEmail(e.target.value);
  const handleSenderNameChange = (e) => setSenderName(e.target.value);
  const handleOtpChange = (e) => setOtp(e.target.value);
  const handleTitleChange = (e) => setTitle(e.target.value);
  const handleMessageChange = (e) => setMessage(e.target.value);
  const handleFileChange = (e) => {
    const selectedFiles = [...e.target.files];
    const totalSize = selectedFiles.reduce((acc, file) => acc + file.size, 0);

    setTotalFileSize(totalSize);

    if (totalSize > MAX_UPLOAD_SIZE) {
      setExceedsLimit(true);
      setFiles([]); // Clear files if the limit is exceeded
    } else {
      setExceedsLimit(false);
      setFiles(selectedFiles);
    }
  };
  const handleCoverPhotoChange = (e) => {
    const selectedFiles = [...e.target.files];

    if (selectedFiles.length > 5) {
      setStatusMessage(t(language, 'coverPhotosLimit'));
      setCoverPhotos([]); // Clear the selection if the limit is exceeded
      e.target.value = ''; // Clear the file input
      return;
    }

    const jpgFiles = selectedFiles.filter((file) => file.type === 'image/jpeg'); // Allow only JPG files

    if (jpgFiles.length !== selectedFiles.length) {
      setStatusMessage(t(language, 'onlyJpgFiles'));
      setCoverPhotos([]); // Reject all files if any non-JPG file is selected
      e.target.value = ''; // Clear the file input
    } else {
      setStatusMessage(''); // Clear the message if all files are valid
      setCoverPhotos(jpgFiles);
    }
  }; // Handle cover photo selection
  const handleCaptchaChange = (token) => {
    setCaptchaToken(token); // Update the reCAPTCHA token
  };
  const handleTermsChange = (e) => setTermsAccepted(e.target.checked); // Handle terms checkbox change

  const sendOtp = async () => {
    // Validation for mandatory fields
    if (!email.trim()) {
      setStatusMessage(t(language, 'missingEmail'));
      return;
    }

    if (exceedsLimit) {
      setStatusMessage(t(language, 'uploadSizeExceeded'));
      return;
    }

    if (files.length === 0) {
      setStatusMessage(t(language, 'pleaseUploadAtLeastOne'));
      return;
    }

    // Invisible Captcha Logic
    let token = captchaToken;
    if (!token && captchaRef.current) {
      try {
        token = await captchaRef.current.executeAsync();
        setCaptchaToken(token);
      } catch (error) {
        console.error('Captcha execution failed:', error);
        setStatusMessage(t(language, 'missingCaptcha'));
        return;
      }
    }

    if (!token) {
      setStatusMessage(t(language, 'missingCaptcha'));
      return;
    }

    try {
      const response = await fetch(`${SERVER_URL}/upload/send-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, senderName, captchaToken: token, language }), // Include selected language + reCAPTCHA token
      });

      if (!response.ok) throw new Error('Failed to send OTP');
      setStep(2);
      setStatusMessage(t(language, 'otpSent'));
    } catch (error) {
      console.error('Error sending OTP:', error);
      setStatusMessage('Error sending OTP.');
      if (captchaRef.current) captchaRef.current.reset(); // Reset captcha on error
      setCaptchaToken(null);
    }
  };

  const verifyOtp = async () => {
    try {
      const response = await fetch(`${SERVER_URL}/upload/verify-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, otp }),
      });

      if (!response.ok) throw new Error('Invalid OTP');
      const result = await response.json();
      // Expect server to return { token, expiresIn } on success
      setUploadAuthToken(result.token); // NEW: store token
      setStep(3);
      setStatusMessage(t(language, 'otpVerified'));
      // PASS the freshly received token to avoid state update race
      startStreamUpload(result.token);
    } catch (error) {
      console.error('Error verifying OTP:', error);
      setStatusMessage(t(language, 'errorVerifyingOtp'));
    }
  };

  const uploadFiles = async () => {
    // CHANGED: renamed to startStreamUpload; keep old reference for minimal diff
    return startStreamUpload(uploadAuthToken);
  };

  // REPLACE old computeOverallEta with parameterized version
  const computeOverallEta2 = (totalBytes, clientLoaded, uploaded, zipped, serverActive) => {
    if (!totalBytes || totalBytes <= 0) return;
    const now = Date.now();
    if (now - lastEtaUpdateRef.current < 1000) return; // throttle 1s
    lastEtaUpdateRef.current = now;

    // Overall start time
    if (!overallStartRef.current) overallStartRef.current = now;

    // Choose progressed bytes
    let progressed = serverActive ? Math.max(uploaded || 0, zipped || 0) : (clientLoaded || 0);
    if (progressed <= 0) return;

    let fraction = progressed / totalBytes;
    if (fraction < 0.001) return; // wait for measurable progress
    fraction = Math.min(fraction, 0.999); // avoid div by near-zero later

    const elapsedSec = (now - overallStartRef.current) / 1000;
    let rawRemaining = elapsedSec * (1 - fraction) / fraction;
    rawRemaining = Math.max(0, rawRemaining);

    // Smoothing (EMA)
    if (smoothedRemainingRef.current == null) {
      smoothedRemainingRef.current = rawRemaining;
    } else {
      const prev = smoothedRemainingRef.current;
      const blended = prev * 0.8 + rawRemaining * 0.2;
      // Prevent large upward jump (> +10%) unless fraction very small
      if (prev > 0 && blended > prev * 1.10 && fraction > 0.05) {
        smoothedRemainingRef.current = prev * 1.05; // cap increase
      } else {
        smoothedRemainingRef.current = blended;
      }
    }

    setOverallTimeRemaining(Math.round(smoothedRemainingRef.current));
  };

  const clearPartSimulation = () => {
    if (partSimRef.current.intervalId) {
      clearInterval(partSimRef.current.intervalId);
    }
    partSimRef.current = {
      inFlight: false,
      partNumber: null,
      partSize: 0,
      startTime: 0,
      durationMs: null,
      lastRealUploadedBytes: uploadedBytes,
      intervalId: null,
      simulatedPct: 0,
      lastPartSpeed: partSimRef.current.lastPartSpeed
    };
    setPartProgressPct(0);
  };

  const sseRef = useRef(null);
  const lastSseActivityRef = useRef(0);
  const [reconnecting, setReconnecting] = useState(false);
  const completionFlagRef = useRef(false);

  const openSse = (sid, token) => { // CHANGED: accept token so EventSource can carry it as query param
    if (sseRef.current) {
      sseRef.current.close();
      sseRef.current = null;
    }
    // Pass token as query param because EventSource cannot set headers
    const url = `${SERVER_URL}/upload/stream-zip/progress/${sid}?token=${encodeURIComponent(token || '')}`;
    const es = new EventSource(url);
    sseRef.current = es;
    lastSseActivityRef.current = Date.now();
    setReconnecting(false);

    es.onmessage = (evt) => {
      lastSseActivityRef.current = Date.now();
      if (!evt.data) return;
      const data = JSON.parse(evt.data);

      if (data.type === 'heartbeat' || data.type === 'init') {
        // just activity marker
        return;
      }
      if (data.type === 'archive-start') {
        // optional hook
        return;
      }
      if (data.type === 'part-start') {
        // ...existing part-start handling...
        clearPartSimulation();
        partSimRef.current.inFlight = true;
        partSimRef.current.partNumber = data.partNumber;
        partSimRef.current.partSize = data.partSize;
        partSimRef.current.startTime = Date.now();
        const estSpeed = uploadSpeedRef.current.speed || partSimRef.current.lastPartSpeed || 5 * 1024 * 1024;
        partSimRef.current.intervalId = setInterval(() => {
          if (!partSimRef.current.inFlight) return;
          const elapsed = Date.now() - partSimRef.current.startTime;
          let pct = (estSpeed * (elapsed / 1000) / partSimRef.current.partSize) * 100;
            pct = Math.min(99, Math.max(1, pct));
          setPartProgressPct(Math.round(pct));
        }, 1000);
        return;
      }
      if (data.type === 'part-complete') {
        partSimRef.current.durationMs = data.durationMs;
        partSimRef.current.lastPartSpeed = data.partSize / (data.durationMs / 1000);
        setPartProgressPct(100);
        setTimeout(() => clearPartSimulation(), 600);
        return;
      }
      if (data.type === 'progress') {
        hasServerProgressRef.current = true;
        setZippedBytes(data.zippedBytes);
        setUploadedBytes(data.uploadedBytes);
        zipSpeedRef.current.bytes = data.zippedBytes;
        uploadSpeedRef.current.bytes = data.uploadedBytes;
        // NEW: mark start times once
        if (data.zippedBytes > 0 && !serverZipStartRef.current) serverZipStartRef.current = Date.now();
        if (data.uploadedBytes > 0 && !serverUploadStartRef.current) serverUploadStartRef.current = Date.now();
        const zipPct = Math.min(100, Math.round((data.zippedBytes / data.totalBytes) * 100));
        setZippingProgress(zipPct);
        const uploadPct = Math.min(100, Math.round((data.uploadedBytes / Math.max(data.zippedBytes, 1)) * 100));
        setUploadProgress(uploadPct);
        computeOverallEta2(
          data.totalBytes,
          clientBytesRef.current.loaded,
          data.uploadedBytes,
          data.zippedBytes,
          true
        ); // CHANGED
        return;
      }
      if (data.type === 'complete') {
        if (completionFlagRef.current) return; // avoid double
        completionFlagRef.current = true;
        clearPartSimulation();
        es.close();
        setZippingProgress(100);
        setUploadProgress(100);
        setClientSendTimeRemaining(0);
        setZippingTimeRemaining(0);
        setUploadTimeRemaining(0);
        // NEW: final overall ETA
        setOverallTimeRemaining(0);
        const downloadLink = `https://clickz.fit/download/${data.token}`;
        const formattedExpiration = new Date(data.expiration).toLocaleString('en-GB', {
          day: '2-digit', month: '2-digit', year: 'numeric',
          hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
        });
        setStatusMessage(
          <>
            {t(language, 'filesUploadedSuccessfully')}
            <br /><br />{t(language, 'downloadButton')}:<br />
            <a href={downloadLink} target="_blank" rel="noopener noreferrer" className="download-link">
              {downloadLink}
            </a>
            <br /><br />{t(language, 'linkExpires')} {formattedExpiration}.
          </>
        );
        setUploadCompleted(true);
        setIsProcessing(false);
        setUploading(false);
        return;
      }
      if (data.type === 'error') {
        clearPartSimulation();
        es.close();
        if (!uploadCompleted) {
          setStatusMessage(t(language, 'errorDuringStreamingUpload'));
          setIsProcessing(false);
          setUploading(false);
        }
        return;
      }
    };

    es.onerror = () => {
      // Let watchdog handle reconnection; just mark activity time to avoid rapid loops
      lastSseActivityRef.current = Date.now();
    };
  };

  // Watchdog: reconnect SSE if idle >12s and not completed
  React.useEffect(() => {
    const interval = setInterval(() => {
      if (!sessionId || completionFlagRef.current) return;
      const idleMs = Date.now() - lastSseActivityRef.current;
      if (idleMs > 12000) {
        setReconnecting(true);
        openSse(sessionId, uploadAuthToken); // CHANGED: pass token on reconnect
      }
    }, 4000);
    return () => clearInterval(interval);
  }, [sessionId, uploadAuthToken]);

  const startStreamUpload = async (authTokenParam) => {
    setUploading(true);
    setZippingProgress(0);
    setUploadProgress(0);
    setStatusMessage(t(language, 'preparingCoverPhotos'));
    setIsProcessing(true);
    zippingStartTime.current = Date.now();
    uploadStartTime.current = Date.now();
    hasServerProgressRef.current = false;
    setClientSendProgress(0);
    setClientSendTimeRemaining(null);
    setOverallTimeRemaining(null);
    serverZipStartRef.current = null;       // NEW reset
    serverUploadStartRef.current = null;    // NEW reset
    clientBytesRef.current = { loaded: 0, total: 0, lastLoaded: 0, lastTime: 0, speed: 0, remaining: null };
    smoothedRemainingRef.current = null;
    clientPhaseStartRef.current = null;
    zipPhaseStartRef.current = null;
    uploadPhaseStartRef.current = null;

    const tokenToUse = authTokenParam || uploadAuthToken;
    if (!tokenToUse) {
      setStatusMessage(t(language, 'missingUploadAuthToken'));
      setIsProcessing(false);
      setUploading(false);
      return;
    }

    try {
      // --- Cover photos (unchanged) ---
      const coverPhotoUrls = [];
      const uploadDate = Date.now();
      const safeEmail = email.replace(/[^a-zA-Z0-9.\-_]/g, '_');

      let counter = 1;
      for (const photo of coverPhotos) {
        const safePhotoName = photo.name.replace(/[^a-zA-Z0-9.\-_]/g, '_');
        const photoFileName = `${uploadDate}_${safeEmail}_${counter}_${safePhotoName}`;
        counter++;

        // Resize the image
        const resizedPhoto = await readAndCompressImage(photo, imageResizeConfig);

        const response = await fetch(`${SERVER_URL}/upload/signed-url`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${tokenToUse}` // use token parameter/state fallback
          },
          body: JSON.stringify({
            email,
            fileName: photoFileName,
            contentType: photo.type,
          }),
        });

        if (!response.ok) throw new Error('Failed signed URL for cover photo');
        const { signedUrl } = await response.json();

        const uploadResponse = await fetch(signedUrl, {
          method: 'PUT',
          headers: { 'Content-Type': photo.type },
          body: resizedPhoto,
        });

        if (!uploadResponse.ok) throw new Error(t(language, 'failedUploadCoverPhoto'));
        coverPhotoUrls.push(`uploads/${photoFileName}`);
      }

      // --- Init streaming zip session ---
      const totalSize = files.reduce((a, f) => a + f.size, 0);
      setStatusMessage(t(language, 'initializingStreamingZipSession'));
      const initResp = await fetch(`${SERVER_URL}/upload/stream-zip/init`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${tokenToUse}`
        },
        body: JSON.stringify({ email, senderName, title, message, totalSize, coverPhotoUrls, language, totalFileSize }) // CHANGED: include language
      });
      if (!initResp.ok) throw new Error(t(language, 'errorDuringStreamingUpload'));
      const { sessionId: sid } = await initResp.json();
      setSessionId(sid);
      completionFlagRef.current = false;
      openSse(sid, tokenToUse); // CHANGED: pass token for SSE query param
      setStatusMessage(t(language, 'sessionCreatedUploadingFilesSequentially'));

      // Concurrent file uploads
      const CONCURRENCY = 4; // CHANGED: Increased to 4 for better speed while keeping load managed
      const fileProgress = new Array(files.length).fill(0);

      const updateClientProgress = () => {
        if (hasServerProgressRef.current) return;
        const totalUploaded = fileProgress.reduce((a, b) => a + b, 0);
        clientBytesRef.current.total = totalSize;
        clientBytesRef.current.loaded = totalUploaded;
        // CHANGED: Use decimal precision for smoother feedback on large uploads
        const pct = (totalUploaded / totalSize) * 100;
        setClientSendProgress(Math.min(Number(pct.toFixed(1)), 99.9));
        computeOverallEta2(totalSize, totalUploaded, uploadedBytes, zippedBytes, false);
      };

      const uploadSingleFile = (file, index) => {
        return new Promise((resolve, reject) => {
          const xhr = new XMLHttpRequest();
          xhr.open('POST', `${SERVER_URL}/upload/stream-zip/file/${sid}`, true);
          xhr.setRequestHeader('Authorization', `Bearer ${tokenToUse}`);
          
          xhr.upload.onloadstart = () => {
            if (index === 0) clientSendStartTime.current = Date.now();
          };
          
          xhr.upload.onprogress = (e) => {
            fileProgress[index] = e.loaded;
            updateClientProgress();
          };

          xhr.onerror = () => reject(new Error(t(language, 'fileUploadError')));
          
          xhr.onload = () => {
            if (xhr.status === 200) {
              fileProgress[index] = file.size;
              updateClientProgress();
              resolve();
            } else if (xhr.status === 401) {
              reject(new Error('401'));
            } else {
              reject(new Error(`Status ${xhr.status}`));
            }
          };

          const fd = new FormData();
          fd.append('file', file, file.name);
          xhr.send(fd);
        });
      };

      // Process files with concurrency limit
      const queue = [...files.map((f, i) => ({ file: f, index: i }))];
      const workers = [];

      const worker = async () => {
        while (queue.length > 0) {
          const { file, index } = queue.shift();
          setStatusMessage(`${t(language, 'uploadingFile')} ${index + 1}/${files.length}`);
          try {
            await uploadSingleFile(file, index);
          } catch (err) {
            if (err.message === '401') {
              setStatusMessage(t(language, 'missingUploadAuthToken'));
              setIsProcessing(false);
              setUploading(false);
              throw err;
            }
            throw err;
          }
        }
      };

      for (let i = 0; i < Math.min(CONCURRENCY, files.length); i++) {
        workers.push(worker());
      }

      await Promise.all(workers);

      // Finalize archive
      setStatusMessage(t(language, 'finalizingArchive'));
      const finishResp = await fetch(`${SERVER_URL}/upload/stream-zip/finish/${sid}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${tokenToUse}` } // use tokenToUse
      });
      if (!finishResp.ok) throw new Error(t(language, 'errorDuringStreamingUpload'));
      // Completion handled via SSE 'complete'
    } catch (err) {
      console.error(err);
      setStatusMessage(t(language, 'errorDuringStreamingUpload'));
      setIsProcessing(false);
      setUploading(false);
      clearPartSimulation();
    }
  };

  const formatTime = (seconds) => {
    if (seconds === null) return '';
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes} ${t(language, 'minutes')} ${remainingSeconds} ${t(language, 'seconds')}`;
  };

  const formatFileSize = (size) => {
    if (size == 0) return '0';
    if (size >= 1024 * 1024 * 1024) 
      return `${(size / (1024 * 1024 * 1024)).toFixed(2)} GB`;
    if (size >= 1024 * 1024) 
      return `${(size / (1024 * 1024)).toFixed(2)} MB`;
    if (size >= 1024) 
      return `${(size / 1024).toFixed(2)} KB`;    
    
    return `${size} bytes`;
  };

  // close on outside click or Escape
  useEffect(() => {
    function onDocClick(e) {
      if (!langOpen) return;
      if (!langTriggerRef.current) return;
      if (langTriggerRef.current.contains(e.target)) return;
      if (langListRef.current && langListRef.current.contains(e.target)) return;
      setLangOpen(false);
    }
    function onKey(e) {
      if (e.key === 'Escape') setLangOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [langOpen]);

  return (
    <div dir={language === 'he' ? 'rtl' : 'ltr'}>
      <div className="top-bar"> {/* Top bar for the title */}
        <h1 className="main-title">Clickz File Transfer</h1>
      </div>
      <div className="upload-container" > {/* CHANGED: set dir based on language */}
        {/* Language combobox (flag trigger + listbox) */}
        <div className="lang-combobox" role="combobox" aria-haspopup="listbox" aria-expanded={langOpen} aria-owns="lang-listbox">
          <button
            ref={langTriggerRef}
            className="lang-trigger"
            aria-label="Select language"
            onClick={() => setLangOpen((s) => !s)}
            onKeyDown={(e) => {
              if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setLangOpen(true);
                setTimeout(() => langListRef.current?.querySelector('[role="option"]')?.focus(), 0);
              }
            }}
          >
            <img
              src={(langOptions.find(o => o.code === language) || langOptions[0]).flag}
              alt={(langOptions.find(o => o.code === language) || langOptions[0]).alt}
              className="lang-flag-img"
            />
          </button>
          {langOpen && (
            <ul id="lang-listbox" ref={langListRef} className="lang-options" role="listbox" tabIndex={-1}>
              {langOptions.map((opt, idx) => (
                <li
                  key={opt.code}
                  role="option"
                  tabIndex={0}
                  aria-selected={language === opt.code}
                  className={`lang-option ${language === opt.code ? 'selected' : ''}`}
                  onClick={() => { setLanguage(opt.code); setLangOpen(false); langTriggerRef.current?.focus(); }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setLanguage(opt.code);
                      setLangOpen(false);
                      langTriggerRef.current?.focus();
                    } else if (e.key === 'ArrowDown') {
                      e.preventDefault();
                      const next = e.currentTarget.nextElementSibling || langListRef.current?.firstElementChild;
                      next?.focus();
                    } else if (e.key === 'ArrowUp') {
                      e.preventDefault();
                      const prev = e.currentTarget.previousElementSibling || langListRef.current?.lastElementChild;
                      prev?.focus();
                    } else if (e.key === 'Escape') {
                      setLangOpen(false);
                      langTriggerRef.current?.focus();
                    }
                  }}
                >
                  <img src={opt.flag} alt={opt.alt} className="lang-flag-img" />
                  <span className="lang-label">{opt.label}</span>
                </li>
              ))}
            </ul>
          )}
        </div>

        <h1 className={uploadCompleted ? 'upload-completed-title' : ''}>
          {uploadCompleted ? t(language, 'uploadCompletedTitle') : t(language, 'uploadTitle')}
        </h1>
        {step === 1 && (
          <div className="upload-form-step">
            <input
              type="email"
              placeholder={t(language, 'yourEmail')}
              value={email}
              onChange={handleEmailChange}
              className="upload-input-field"
            />
            <input
              type="text"
              placeholder={t(language, 'yourNameOptional')}
              value={senderName}
              onChange={handleSenderNameChange}
              className="upload-input-field"
            />
            <input
              type="text"
              placeholder={t(language, 'titleOptional')}
              value={title}
              onChange={handleTitleChange}
              className="upload-input-field"
            />
            <textarea
              placeholder={t(language, 'personalMessageOptional')}
              value={message}
              onChange={handleMessageChange}
              className="upload-textarea-field"
            />
            <label className="upload-label">{t(language, 'uploadFilesLabel')}</label>
            <div className="file-input-wrapper">
              <label className="file-input-button" htmlFor="file-input-main">{t(language, 'chooseFiles')}</label>
              <input
                id="file-input-main"
                type="file"
                multiple
                onChange={handleFileChange}
                className="upload-file-input-hidden"
                aria-label={t(language, 'uploadFilesLabel')}
              />
              <span className="file-input-names">
                {files && files.length > 0 ? `${files.length} ${t(language, 'filesChosen')}` : t(language, 'noFilesChosen')}
              </span>
            </div>
            <div className="file-size-indicator">
              <p>
                {formatFileSize(totalFileSize)} {t(language, 'of')} {formatFileSize(MAX_UPLOAD_SIZE)}
              </p>
              {exceedsLimit && (
                <p className="exceeds-limit-message" style={{ color: 'red', marginBottom: '5px' }}>
                  The total upload size exceeds the 20GB limit.
                </p>
              )}
              <div className="progress-bar-wrapper">
                <div
                  className="progress-bar"
                  style={{
                    width: `${(totalFileSize / MAX_UPLOAD_SIZE) * 100}%`,
                    backgroundColor: exceedsLimit ? 'red' : '#4caf50',
                  }}
                />
              </div>
            </div>
            <label className="upload-label">{t(language, 'uploadCoverPhotosLabel')}</label>
            {statusMessage && statusMessage.includes('Only JPG files') && (
              <p className="exceeds-limit-message" style={{ color: 'red', marginBottom: '5px' }}>
                {t(language, 'onlyJpgFiles')}
              </p>
            )}
            <div className="file-input-wrapper">
              <label className="file-input-button" htmlFor="cover-photo-input">{t(language, 'chooseFiles')}</label>
              <input
                id="cover-photo-input"
                type="file"
                multiple
                accept="image/jpeg"
                onChange={handleCoverPhotoChange}
                className="upload-file-input-hidden"
                aria-label={t(language, 'uploadCoverPhotosLabel')}
              />
              <span className="file-input-names">
                {coverPhotos && coverPhotos.length > 0 ? coverPhotos.length == 1 ? `${coverPhotos.length} ${t(language, 'fileChosen')}` : `${coverPhotos.length} ${t(language, 'filesChosen')}` : t(language, 'noFilesChosen')}
              </span>
            </div>
            
            <div className="upload-recaptcha">
              <ReCAPTCHA
                ref={captchaRef}
                sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
                size="invisible"
                onChange={handleCaptchaChange}
              />
            </div>
            
            <label className="terms-checkbox">
              <input
                type="checkbox"
                checked={termsAccepted}
                onChange={handleTermsChange}
              />
              {t(language, 'termsText')}&nbsp;
              <a href={`/terms?lang=${encodeURIComponent(language)}`} target="_blank" rel="noopener noreferrer">Terms</a>
            </label>
            <button
              onClick={sendOtp}
              className="upload-btn-primary"
              disabled={!termsAccepted} // Disable button if terms are not accepted
            >
              {t(language, 'sendButton')}
            </button>
          </div>
        )}
        {step === 2 && (
          <div className="upload-form-step">
            <input
              type="text"
              placeholder={t(language, 'enterOtpPlaceholder')}
              value={otp}
              onChange={handleOtpChange}
              className="upload-input-field"
            />
            <button onClick={verifyOtp} className="upload-btn-primary">
              {t(language, 'verifyOtpButton')}
            </button>
          </div>
        )}
        {step === 3 && (
          <div className="upload-form-step">
            {uploadCompleted ? (
              <div className="success-animation">
                <div className="checkmark"></div>
              </div>
            ) : (
              <>
                {isProcessing && <div className="spinner"></div>} {/* Spinner */}
                {/* NEW: Client-to-server sending progress before server reports progress */}
                {!hasServerProgressRef.current && clientSendProgress > 0 && clientSendProgress < 100 && (
                  <div className="progress-container">
                    <p>{t(language, 'uploadingFiles')}</p>
                    <div className="progress-bar-wrapper">
                      <div className="progress-bar" style={{ width: `${clientSendProgress}%` }} />
                    </div>
                    <p className="progress-bar-percentage">{clientSendProgress}%</p>
                  </div>
                )}

                {/* Server-side zipping progress */}
                {hasServerProgressRef.current && zippingProgress < 100 && (
                  <div className="progress-container">
                    <p className="progress-bar-p">{t(language, 'uploadingFiles')}</p>
                    <div className="progress-bar-wrapper">
                      <div className="progress-bar" style={{ width: `${zippingProgress}%` }} />
                    </div>
                    <p className="progress-bar-percentage">{zippingProgress}%</p>
                  </div>
                )}
                {/* Wasabi upload progress */}
                {hasServerProgressRef.current && zippingProgress === 100 && uploadProgress < 100 && (
                  <div className="progress-container">
                    <p className="progress-bar-p">{t(language, 'uploadingFiles')}</p>
                    <div className="progress-bar-wrapper">
                      <div className="progress-bar" style={{ width: `${uploadProgress}%` }} />
                    </div>
                    <p className="progress-bar-percentage">{uploadProgress}%</p>
                  </div>
                )}
                {hasServerProgressRef.current && zippingProgress === 100 && uploadProgress < 100 && partSimRef.current.inFlight && (
                  <div className="progress-container">
                    <p className="progress-bar-p">{t(language, 'uploadingZipPart')} {partSimRef.current.partNumber}</p>
                    <div className="progress-bar-wrapper">
                      <div className="progress-bar" style={{ width: `${partProgressPct}%`, backgroundColor: '#2196f3' }} />
                    </div>
                    <p className="progress-bar-percentage">{partProgressPct}%</p>
                  </div>
                )}                
                {reconnecting && !uploadCompleted && (
                  <p style={{ color: '#ff9800', marginTop: '10px' }}>Reconnecting to progress stream...</p>
                )}

                {/* Overall ETA line (compact) */}
                {(clientSendProgress > 0 || hasServerProgressRef.current) && overallTimeRemaining !== null && !uploadCompleted && (
                  <p className="progress-bar-p" style={{ marginTop: '4px' }}>
                    {t(language, 'estimatedTimeRemaining')}: {formatTime(overallTimeRemaining)}
                  </p>
                )}
              </>
            )}
          </div>
        )}
        {statusMessage && <p className="upload-status-message">{statusMessage}</p>}

        {/* Branding */}
        
      </div>
      <div className="upload-branding">
          <img src="/clickzLogo.png" alt="Clickz Logo" className="upload-branding-logo" />
          <p className="upload-branding-text">{t(language, 'poweredBy')}</p>
        </div>
    </div>
  );
};

export default Upload;
